/**
 * `/` for signed-out visitors — the Collect product landing
 * (D-1 A: landing, /login and the app on one domain, collect.qodoonect.com).
 *
 * A product page, not a brand board (Laura, 2026-10-13): what Collect is and who it is
 * for, how it works (documents → wiki → capsules), what can be used today, how data is
 * protected and how to start. Every claim maps to code; the claim → file map is in the
 * PR that made this page. The former brand-concept section is internal now
 * (`synapse-collect/brand/concepts.md`). Claims follow D-3 A: web capsules are
 * compressed and hashed, not encrypted; what is not built yet says "planned".
 */
import MotionBackground from "./MotionBackground";
import { CollectMark, LandingThemeProvider, ThemeToggle, useLandingTheme } from "./theme";
import { MotionConfig } from "motion/react";
import { HeroSection } from "./HeroSection";
import { ProductSection } from "./ProductSection";
import { ProcessFlow, STAGES } from "./ProcessFlow";
import { FeaturesSection } from "./FeaturesSection";
import { SecuritySection } from "./SecuritySection";
import { StartSection } from "./StartSection";

// The background flow — documents → wiki nodes → capsules — in the same stage colours as
// the "How it works" cards (ProcessFlow STAGES).
const FLOW = {
  dark: { ink: "#F0FDF4", accent: "#BB86FC", accent2: "#F2C94C", accent3: "#34D399" },
  light: { ink: "#064E3B", accent: "#6D28D9", accent2: "#A16207", accent3: "#047857" },
};

/** Section anchors in the nav; shown from `lg` so the sign-in button always fits. */
export const NAV = [
  { href: "#product", label: "Product" },
  { href: "#how", label: "How it works" },
  { href: "#security", label: "Security" },
  { href: "#start", label: "Start" },
];

export function Landing(props: { passSignIn: boolean; onSignIn: () => void }) {
  return (
    <LandingThemeProvider>
      {/* prefers-reduced-motion: motion drops its transform animations (entrances), like the CSS rule */}
      <MotionConfig reducedMotion="user">
        <LandingBody {...props} />
      </MotionConfig>
    </LandingThemeProvider>
  );
}

function LandingBody({ passSignIn, onSignIn }: { passSignIn: boolean; onSignIn: () => void }) {
  const { theme } = useLandingTheme();
  const signInLabel = passSignIn ? "Sign in with Ordinal Pass" : "Sign in";
  return (
    <div className={theme === "light" ? "collect-light relative min-h-screen" : "relative min-h-screen"}>
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:px-4 focus:py-3" style={{ background: "#10B981", color: "#050505" }}>
        Skip to content
      </a>
      {/* Documents -> wiki -> capsules, over the original atmosphere (index.html .atmosphere-bg). */}
      <div className="collect-flow fixed inset-0 z-0 pointer-events-none" aria-hidden="true">
        <MotionBackground className="absolute inset-0" variant="capsules" palette={FLOW[theme]} intensity={0.9} interactive />
      </div>
      <div className="relative z-10">
        <Navigation onSignIn={onSignIn} />
        <main id="main" tabIndex={-1} className="outline-none">
          <HeroSection passSignIn={passSignIn} signInLabel={signInLabel} onSignIn={onSignIn} />
          {/* Below the hero a light veil quiets the flow behind long text; the atmosphere still shows. */}
          <div className="collect-veil">
            <ProductSection />
            <ProcessFlow />
            <FeaturesSection />
            <SecuritySection passSignIn={passSignIn} />
            <StartSection passSignIn={passSignIn} signInLabel={signInLabel} onSignIn={onSignIn} />
          </div>
        </main>
        <Footer />
      </div>
    </div>
  );
}

function Navigation({ onSignIn }: { onSignIn: () => void }) {
  return (
    <nav aria-label="Primary" className="collect-nav fixed top-0 left-0 w-full z-50 px-4 md:px-6 py-3 md:py-4 flex items-center justify-between gap-3 backdrop-blur-md">
      <a href="/" className="flex items-center gap-2.5" aria-label="Collect home">
        <CollectMark size={30} />
        <span className="hidden min-[370px]:inline font-mono text-sm tracking-widest uppercase">Collect</span>
      </a>
      <div className="flex items-center gap-3 md:gap-5 text-sm font-mono tracking-widest uppercase">
        {NAV.map((n) => (
          <a key={n.href} href={n.href} className="hidden lg:inline opacity-60 hover:opacity-100 transition-opacity whitespace-nowrap">
            {n.label}
          </a>
        ))}
        <a
          href="https://qodoonect.com"
          target="_blank"
          rel="noreferrer"
          className="hidden sm:inline px-4 py-2 border border-white/20 rounded-full hover:bg-white hover:text-black transition-all"
        >
          Group
        </a>
        <ThemeToggle />
        <button
          type="button"
          onClick={onSignIn}
          className="px-4 py-2 rounded-full transition-all whitespace-nowrap"
          style={{ background: "#10B981", color: "#050505" }}
        >
          Sign in
        </button>
      </div>
    </nav>
  );
}

function Footer() {
  const { tone } = useLandingTheme();
  return (
    <footer className="collect-veil-flat py-16 border-t border-white/10 px-6 md:px-12">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
        <div className="flex items-center gap-3 opacity-60">
          <CollectMark size={22} />
          <span className="font-mono text-sm tracking-widest uppercase">Collect · Qodoonect Group</span>
        </div>
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/60">
          {STAGES.map((s, i) => (
            <span key={s.n}>
              {i > 0 && <span className="mx-2" aria-hidden="true">→</span>}
              <span style={{ color: tone(s.color) }}>{s.name}</span>
            </span>
          ))}
        </p>
        <div className="text-xs font-light text-white/60">
          &copy; {new Date().getFullYear()} Qodoonect. Lithuania, EU.
        </div>
      </div>
    </footer>
  );
}
